import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import { stableStringify } from '@Utils/searchParams';

interface ReferralResponse {
  data: {
    subscriptions: {
      fish: {
        phoneNumber: string;
        fishProfiles: {
          fName: string;
          lName: string;
        }[];
      };
      subscriptionType: string;
      registrationCode: {
        code: string;
      };
    }[];
  }[];
}

type ReferralsResponse = {
  data: ReferralResponse;
  meta: Record<string, any>;
};
type DefaultError = unknown;

type UseReferralsParams<S extends Record<string, any> = Record<string, any>> = {
  /** Optional query string params to forward to service */
  searchParams?: S;
  /** Override default enabled behavior */
  enabled?: boolean;
};

export function useReferrals<TData = ReferralsResponse, TError = DefaultError>(
  { searchParams, enabled = true }: UseReferralsParams = {},
  options?: Omit<
    UseQueryOptions<ReferralsResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const stableParams = useMemo(
    () => stableStringify(searchParams),
    [searchParams],
  );

  return useQuery<ReferralsResponse, TError, TData>({
    queryKey: ['referrals', stableParams],
    enabled: enabled,
    queryFn: async () => {
      return service.getReferrals(searchParams ?? {});
    },
    // caller can pass select, staleTime, gcTime, retry, refetchOnWindowFocus, etc.
    ...(options as any),
  });
}
